import type { ReactNode } from 'react';
import { Page } from '@/app/Shell';
import { Icon } from '@/components/lumen/Icon';
import { useSources } from '@/lib/api/integrations';
import { useDeviceCounts } from '@/lib/api/onboarding';
import { firstSiteSteps, type FirstSiteStepId } from './first-site';

/**
 * What a site screen shows while the organisation has no site — three steps instead of zeros.
 * `first-site.ts` holds the rule for when, and the step logic; this is only the drawing.
 *
 * Numbered because it IS a sequence: a feed can only be assigned to a site that exists, and a
 * device only integrated from a feed that is assigned. A step that cannot be taken yet shows no
 * button — a control that cannot act is hidden, not disabled.
 */
const COPY: Record<FirstSiteStepId, { title: string; body: string; action: string }> = {
  site: {
    title: 'Add your site',
    body: 'Name it, place it on the map and say what kind of site it is.',
    action: 'Add a site',
  },
  gateway: {
    title: 'Connect its gateway',
    body: 'Assign the feed your gateway publishes on to the site. Until then its data is discarded.',
    action: 'Open gateways',
  },
  devices: {
    title: 'Choose which devices to monitor',
    body: 'Integrate the devices the gateway reported. Their readings are stored from then on.',
    action: 'Open discovered devices',
  },
};

export function FirstSiteSteps({
  title,
  siteCount,
  canAddSite,
  onAddSite,
  onOpenGateways,
  onOpenDevices,
}: {
  /** The screen the person opened — its title stays, so they know where they are. */
  title: string;
  siteCount: number;
  /** `sites.create`. Without it step 1 says who to ask instead of offering a button that fails. */
  canAddSite: boolean;
  onAddSite: () => void;
  onOpenGateways: () => void;
  onOpenDevices: () => void;
}): ReactNode {
  const feeds = useSources();
  const counts = useDeviceCounts();
  const steps = firstSiteSteps({
    siteCount,
    assignedFeeds: feeds.data?.counts.assigned ?? 0,
    integratedDevices: counts.data?.counts.allowed ?? 0,
  });
  const act: Record<FirstSiteStepId, () => void> = {
    site: onAddSite,
    gateway: onOpenGateways,
    devices: onOpenDevices,
  };

  return (
    <Page title={title} desc="This organisation has no site yet">
      <section className="lm-card max-w-[720px] p-[16px]" aria-label="Set up your first site">
        <h2 className="lm-t-base font-semibold text-[var(--fg)]">Set up your first site</h2>
        <p className="lm-t-sm mt-[2px] text-[var(--muted-fg)]">
          Everything here is measured per site. Three steps and this screen fills in.
        </p>
        <ol className="mt-[14px] flex flex-col">
          {steps.map((step, i) => {
            const copy = COPY[step.id];
            return (
              <li
                key={step.id}
                className="flex items-start gap-[12px] border-t border-[var(--border)] py-[12px] first:border-t-0"
              >
                <span
                  className={`lm-num mt-[1px] flex size-[22px] shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold ${
                    step.next
                      ? 'border-[var(--fg)] text-[var(--fg)]'
                      : 'border-[var(--border)] text-[var(--muted-fg)]'
                  }`}
                  aria-hidden
                >
                  {step.done ? <Icon name="ic-check" size={12} /> : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-[13px] font-semibold ${
                      step.done || step.blocked ? 'text-[var(--muted-fg)]' : 'text-[var(--fg)]'
                    }`}
                  >
                    {copy.title}
                    {step.done && <span className="sr-only"> — done</span>}
                  </p>
                  <p className="lm-t-sm mt-[2px] text-[var(--muted-fg)]">
                    {step.id === 'site' && !canAddSite
                      ? 'Ask your administrator to add a site. Your role does not create them.'
                      : copy.body}
                  </p>
                </div>
                {!step.done && !step.blocked && (step.id !== 'site' || canAddSite) && (
                  <button
                    type="button"
                    onClick={act[step.id]}
                    className={`lm-press shrink-0 rounded-[var(--radius-sm)] px-[11px] py-[6px] text-[12px] font-semibold ${
                      step.next
                        ? 'bg-[var(--primary)] text-[var(--primary-fg)]'
                        : 'lm-hov border border-[var(--border)] text-[var(--fg)]'
                    }`}
                  >
                    {copy.action}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </section>
    </Page>
  );
}
